/**
 * F2 quick-add (CargoBooks' quick-add pop-ups). F2 in a lookup (autocomplete.ts raises kit:quick-create) opens that
 * record's setup form in a dialog, with what was typed filled into its first field. Saving a new record there
 * (?quick=1: the server sends quickCreated {id, text} instead of a redirect) closes the dialog and picks the record in
 * the lookup. Esc closes it. The server still checks the Add right, so a user without it just gets the refusal toast.
 */

/** Lookup endpoint → the setup form that adds one. */
const FORMS: Record<string, string> = {
  "account-groups": "/Backoffice/AccountGroup/Edit",
  agents: "/Backoffice/Agent/Edit",
  countries: "/Backoffice/Country/Edit",
  descriptions: "/Backoffice/Description/Edit",
  drivers: "/Backoffice/Driver/Edit",
  godowns: "/Backoffice/Godown/Edit",
  ledgers: "/Backoffice/Ledger/Edit",
  narrations: "/Backoffice/Narration/Edit",
  parties: "/Backoffice/Party/Edit",
  "place-groups": "/Backoffice/PlaceGroup/Edit",
  places: "/Backoffice/Place/Edit",
  subledgers: "/Backoffice/Subledger/Edit",
  trucks: "/Backoffice/Truck/Edit",
};

type Saved = { id: string; text: string };

function open(input: HTMLInputElement, resource: string) {
  const form = FORMS[resource.match(/\/api\/v1\/([a-z-]+)\//)?.[1] ?? ""];
  if (!form) return;
  const url = new URL(form, location.origin);
  url.searchParams.set("quick", "1");
  if (input.value.trim()) url.searchParams.set("q", input.value.trim());
  // The lookup's own filters (e.g. data-kit-ac-param-ledger_id) preset the new record (a sub-ledger under that ledger).
  for (const a of Array.from(input.attributes).filter((x) => x.name.startsWith("data-kit-ac-param-"))) {
    const value = (input.form ?? document).querySelector<HTMLInputElement>(`[name='${a.value}']`)?.value;
    if (value) url.searchParams.set(a.name.slice("data-kit-ac-param-".length), value);
  }

  const dialog = document.createElement("dialog");
  dialog.className = "kit-quick-dialog";
  const frame = document.createElement("iframe");
  frame.title = "Add a record";
  frame.src = url.pathname + url.search;
  dialog.append(frame);
  document.body.append(dialog);
  dialog.showModal();
  frame.addEventListener("load", () => frame.contentWindow?.focus());

  const close = (saved?: Saved) => {
    window.removeEventListener("message", onMessage);
    dialog.remove();
    input.focus();
    if (saved) input.dispatchEvent(new CustomEvent("kit:ac-accept", { detail: saved }));
  };
  const onMessage = (m: MessageEvent) => {
    if (m.origin !== location.origin || m.source !== frame.contentWindow) return;
    if (m.data?.kitQuickCreated) close(m.data.kitQuickCreated as Saved);
    else if (m.data?.kitQuickCancel) close();
  };
  window.addEventListener("message", onMessage);
  dialog.addEventListener("cancel", (e) => { e.preventDefault(); close(); });
}

document.addEventListener("kit:quick-create", (e) => {
  const input = e.target;
  if (input instanceof HTMLInputElement) open(input, (e as CustomEvent<{ resource?: string }>).detail?.resource ?? "");
});

// Inside the dialog: fill the first field with what was typed, and tell the opener when the record is saved.
if (window.parent !== window && document.body?.classList.contains("kit-quick")) {
  const q = new URLSearchParams(location.search).get("q");
  const first = document.querySelector<HTMLInputElement>("main input[autofocus]");
  if (q && first && !first.value) first.value = q;
  document.body.addEventListener("quickCreated", (e) => {
    const { id, text } = (e as CustomEvent<Saved>).detail; // htmx adds the element to detail, which can't be posted
    window.parent.postMessage({ kitQuickCreated: { id, text } }, location.origin);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !e.defaultPrevented) window.parent.postMessage({ kitQuickCancel: true }, location.origin);
  });
}
